<template>
  <div class="dn-ticker-web-socket">
    <div class="container">
      <div class="button">
        <a :href="investorTicker.homePageURL">
          <span>Investor</span>
          <dn-icon
            name="right-arrow"
            class="arrow"
          />
        </a>
      </div>
      <div class="item-content">
        <a
          v-for="{ name, value, percent, url, changed, decreased } in tickerItems"
          :key="name"
          :class="['item', decreased ? 'item-decreased' : 'item-increased']"
          :href="url"
        >
          <div class="item-holder">
            <span class="item-name">{{ name }}</span>
            <span class="item-value">{{ value }}</span>
          </div>
          <div
            v-if="changed"
            :class="['item-difference-holder', decreased ? 'item-difference-holder-decreased' : 'item-difference-holder-increased']"
          >
            <dn-icon
              name="triangle"
              class="icon"
            />
            <span
              v-if="percent"
              :class="['item-difference', 'item-percentage', decreased ? 'item-difference-decreased' : 'investor-bar__item-difference-increased']"
            >
              {{ percent }}%
            </span>
          </div>
        </a>
      </div>
      <div class="button">
        <a
          :href="investorTicker.editPageURL"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useProfileLoginStore } from '~/store/profileLogin';
import type { InvestorTickerItem } from '~/types';

const profileLoginStore = useProfileLoginStore();
const tickerItems = ref<InvestorTickerItem[]>([]);
let isInitialData = true;
let socket: WebSocket | null = null;

const props = defineProps({
  investorTicker: {
    type: Object,
    required: true,
    default: () => {}
  }
});

onMounted(() => {
  if (socket === null) {
    socket = new WebSocket(props.investorTicker.streamURL);
    socket.onopen = onOpen;
    socket.onmessage = onMessage;
  }
});

onUnmounted(() => {
  if (socket) {
    socket.close();
    socket = null;
  }
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onOpen (event: any) {
  isInitialData = true;
  const reqData = profileLoginStore.loginStatus.isLoggedIn && profileLoginStore.loginStatus.ncpIdHash
    ? {
        seq: 0,
        d: {
          _t: 'watchlist',
          id: profileLoginStore.loginStatus.ncpIdHash
        }
      }
    : {
        seq: 0,
        d: {
          _t: 'watchlist'
        }
      };
  socket?.send(JSON.stringify(reqData));
}

function onMessage (event: any) {
  mapInvestorTicker(JSON.parse(event.data));
}

function calculatedLastValue (quant: string, last: number) {
  const currencyMultiplier = props.investorTicker.currencyMultiplier;
  const multiplier = currencyMultiplier[quant] || 1;
  return last * multiplier;
}

function toFixed (num: number, digits: number) {
  return parseFloat(num.toFixed(digits));
}

function indexOfAddedItem (items: InvestorTickerItem[], quant: string) {
  let index = -1;
  for (let i = 0; i < items.length; i++) {
    if (items[i].quant === quant) {
      index = i;
      break;
    }
  }
  return index;
}

function isItemChanged (prevItem: InvestorTickerItem, newItem: InvestorTickerItem) {
  return prevItem.value !== newItem.value || prevItem.percent !== newItem.percent;
}

function getMappedItem (key: string, value: any) {
  const item: InvestorTickerItem = {
    quant: key,
    time: value.time,
    value: new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2 }).format(toFixed(calculatedLastValue(key, value.last), 2)),
    percent: value.percent ? new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2 }).format(toFixed(value.percent, 2)) : null,
    changed: !!value.percent,
    increased: value.percent ? value.percent > 0 : false,
    decreased: value.percent ? value.percent < 0 : false
  };
  if (value.name) { item.name = value.name; }
  if (value.url) { item.url = value.url; }
  return item;
}

function mapInvestorTicker (data: any) {
  if (Object.keys(data)[0] === 'seq' && Object.values(data)[0] === 0) { return; }
  let mappedTickerItems = [...tickerItems.value];
  let isChanged = false;
  if (isInitialData) {
    isInitialData = false;
    mappedTickerItems = [];
  }
  Object.entries(data).forEach(([key, value]) => {
    if ((value as any).last) {
      const item = getMappedItem(key, value);
      const index = indexOfAddedItem(mappedTickerItems, key);
      if (index === -1) {
        isChanged = true;
        mappedTickerItems.push(item);
      } else if (isItemChanged(mappedTickerItems[index], item)) {
        isChanged = true;
        const targetItem = { ...mappedTickerItems[index] };
        mappedTickerItems.splice(index, 1, Object.assign(targetItem, item));
      }
    }
  });
  if (isChanged) {
    if (mappedTickerItems.length > 6) {
      mappedTickerItems.splice(6, mappedTickerItems.length - 6);
    }
    tickerItems.value = mappedTickerItems;
  }
}

</script>

<style scoped lang="scss">
.dn {
  &-ticker {
    &-web-socket {
      --dn_font-size: var(--typography-utility-s-font-size);
      --dn_line-height: 12px;

      min-height: 54px;

      .container {
        gap: var(--ui-gap);
      }

      .arrow {
        --dn-icon_color: #FFF;

        @media only screen and (width < 1280px) {
          display: none;
        }
      }

      .item {
        --dn_font-family: var(--typography-utility-s-font-family);
        --dn_font-weight: 500;
        --dn-icon_width: 9px;

        align-items: center;
        border-radius: 4px;

        &-content {
          --dn_text-transform: uppercase;

          gap: var(--ui-gap);
          font-variant-numeric: tabular-nums;
        }

        &-increased {
          border: 1px solid var(--color-investor-positive-bg);
        }

        &-decreased {
          border: 1px solid var(--color-investor-negative-bg);
        }

        &-holder {
          padding: 0 8px;
        }

        &-difference {
          padding-left: 4px;

          &-holder {
            padding: 6px 8px;

            &-increased {
              --dn_color: var(--color-investor-fg);

              background: var(--color-investor-positive-bg);

              svg {
                fill: var(--color-investor-fg);
              }
            }

            &-decreased {
              --dn_color: var(--color-investor-fg);

              background: var(--color-investor-negative-bg);

              svg {
                transform: rotate(180deg);
                fill: var(--color-investor-fg);
              }
            }
          }
        }
      }

      .button {
        a {
          display: flex;
          align-items: center;
          gap: 4px;
          text-decoration: none;
        }

        @media only screen and (width >= 1024px) {
          flex: 0 0 110px;
        }
      }
    }
  }
}
</style>
